import { createDrawerTabController, createRecentSearchStore } from './component-search-shared.js';

const ROOT_SELECTOR = '[data-search-page]';
const controllers = new Map();

class SearchPageController {
  constructor(root) {
    this.root = root;
    this.form = null;
    this.input = null;
    this.clearButton = null;
    this.recentGroup = null;
    this.recentList = null;
    this.clearRecentButton = null;
    this.defaultTab = 'suggestions';
    this.recentMax = 4;

    this.tabController = null;
    this.recentStore = null;

    this.onInput = this.onInput.bind(this);
    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.onFormReset = this.onFormReset.bind(this);
    this.onClick = this.onClick.bind(this);
  }

  init() {
    this.form = this.root.querySelector('[data-search-page-form]');
    this.input = this.root.querySelector('[data-search-page-input]');
    this.clearButton = this.root.querySelector('[data-search-page-clear]');
    this.recentGroup = this.root.querySelector('[data-search-page-recent-group]');
    this.recentList = this.root.querySelector('[data-search-page-recent-list]');
    this.clearRecentButton = this.root.querySelector('[data-search-page-recent-clear]');

    this.defaultTab = (this.root.dataset.defaultTab || 'suggestions').trim();

    const parsedRecentMax = Number.parseInt(this.root.dataset.recentMax || '4', 10);
    if (Number.isFinite(parsedRecentMax) && parsedRecentMax > 0) {
      this.recentMax = parsedRecentMax;
    }

    this.recentStore = createRecentSearchStore({ maxItems: this.recentMax });

    if (this.form) {
      this.form.addEventListener('submit', this.onFormSubmit);
      this.form.addEventListener('reset', this.onFormReset);
    }

    if (this.input) {
      this.input.addEventListener('input', this.onInput);
    }

    this.root.addEventListener('click', this.onClick);

    this.tabController = createDrawerTabController(this.root, {
      defaultTab: this.defaultTab
    });

    this.syncClearButton();
    this.renderRecentSearches();
    return true;
  }

  destroy() {
    if (this.form) {
      this.form.removeEventListener('submit', this.onFormSubmit);
      this.form.removeEventListener('reset', this.onFormReset);
    }

    if (this.input) {
      this.input.removeEventListener('input', this.onInput);
    }

    this.root.removeEventListener('click', this.onClick);

    if (this.tabController) {
      this.tabController.destroy();
      this.tabController = null;
    }
  }

  onInput() {
    this.syncClearButton();
  }

  onFormSubmit() {
    if (!this.input || !this.recentStore) return;
    this.recentStore.push(this.input.value);
  }

  onFormReset() {
    window.requestAnimationFrame(() => this.syncClearButton());
  }

  onClick(event) {
    if (!(event.target instanceof Element)) return;

    const clearRecent = event.target.closest('[data-search-page-recent-clear]');
    if (clearRecent) {
      event.preventDefault();
      this.recentStore?.clear();
      this.renderRecentSearches();
      return;
    }

    const termTrigger = event.target.closest('[data-search-term]');
    if (termTrigger) {
      event.preventDefault();

      const term = (termTrigger.getAttribute('data-search-term') || '').trim();
      if (!term || !this.input || !this.form) return;

      this.input.value = term;
      this.syncClearButton();
      this.recentStore?.push(term);

      if (typeof this.form.requestSubmit === 'function') {
        this.form.requestSubmit();
      } else {
        this.form.submit();
      }
    }
  }

  syncClearButton() {
    if (!this.clearButton || !this.input) return;
    this.clearButton.hidden = this.input.value.trim().length === 0;
  }

  renderRecentSearches() {
    if (!this.recentGroup || !this.recentList) return;

    const recentTerms = this.recentStore ? this.recentStore.get() : [];
    this.recentList.innerHTML = '';

    if (recentTerms.length === 0) {
      this.recentGroup.hidden = true;
      if (this.clearRecentButton) {
        this.clearRecentButton.hidden = true;
      }
      return;
    }

    const fragment = document.createDocumentFragment();

    recentTerms.forEach((term) => {
      const item = document.createElement('li');
      item.className = 'search-page__link-item';

      const button = document.createElement('button');
      button.className = 'search-page__term-btn';
      button.type = 'button';
      button.setAttribute('data-search-term', term);
      button.textContent = term;

      item.appendChild(button);
      fragment.appendChild(item);
    });

    this.recentList.appendChild(fragment);
    this.recentGroup.hidden = false;
    if (this.clearRecentButton) {
      this.clearRecentButton.hidden = false;
    }
  }
}

const initRoot = (root) => {
  if (!root || controllers.has(root)) return;

  const controller = new SearchPageController(root);
  if (controller.init()) {
    controllers.set(root, controller);
  }
};

const destroyRoot = (root) => {
  const controller = controllers.get(root);
  if (!controller) return;
  controller.destroy();
  controllers.delete(root);
};

const initAll = (scope = document) => {
  scope.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
};

const destroyInSection = (sectionRoot) => {
  if (!sectionRoot) return;
  sectionRoot.querySelectorAll(ROOT_SELECTOR).forEach(destroyRoot);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}

document.addEventListener('shopify:section:load', (event) => {
  const sectionRoot = event.target.closest('.shopify-section') || event.target;
  if (!sectionRoot) return;

  destroyInSection(sectionRoot);
  initAll(sectionRoot);
});

document.addEventListener('shopify:section:unload', (event) => {
  const sectionRoot = event.target.closest('.shopify-section') || event.target;
  destroyInSection(sectionRoot);
});
